import { useState } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';
import { LEVELS } from '../../constants/levels.js';
import { SKILL_MAP } from '../../lib/bkt.js';
import { getPace, getWeekDates } from '../../lib/utils.js';
import { getParentEmail, sendEmail, buildReadingLogHTML, buildReportCardHTML } from '../../lib/email.js';
import { isWordMastered } from '../../lib/leitner.js';
import { getSpellingMastery } from '../../lib/spelling.js';
import { getAppTier } from '../../lib/stories.js';
import { getLevelName } from '../../config/levelConfig.js';
import { jsPDF } from 'jspdf';
import { fadeIn, tapScale } from '../../lib/motionPresets.js';

function computeStreak(log){const daySet=new Set((log||[]).map(e=>{const d=new Date(e.t);d.setHours(0,0,0,0);return d.getTime();}));const days=[...daySet].sort((a,b)=>b-a);if(!days.length)return 0;let streak=1;for(let i=1;i<days.length;i++){const diff=(days[i-1]-days[i])/86400000;if(Math.round(diff)===1)streak++;else break;}return streak;}

function generateReadingLogPDF(name, age, tracker, childId, weekOffset) {
  
  
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pw = 215.9, m = 20;
  const days = getWeekDates(weekOffset || 0);
  const lv = LEVELS[tracker.currentLevel] || LEVELS[3];
  const levelName = getLevelName(tracker.currentLevel);
  const modeLabels = { phonics: 'Phonics Practice', sight: 'Sight Words', stories: 'Story Reading', bubbles: 'Bubble Pop' };
  const fmtD = (d, opts) => d.toLocaleDateString('en-US', opts || { month: 'short', day: 'numeric' });
  let y = m;

  // Header
  doc.setFillColor(255, 107, 53);
  doc.rect(m, y, pw - 2 * m, 20, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text('FINLEY WEEKLY READING LOG', pw / 2, y + 9, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('finleyreads.com  |  Built on the Science of Reading', pw / 2, y + 16, { align: 'center' });
  y += 26;

  // Student info
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const ws = days[0], we = days[6];
  doc.text('Student: ' + name, m, y);
  doc.text('Week: ' + fmtD(ws) + ' - ' + fmtD(we, { month: 'short', day: 'numeric', year: 'numeric' }), pw / 2, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Age: ' + age + '  |  Reading Stage: ' + levelName + ' (' + lv.grade + ' equivalent)', m, y);
  y += 10;

  // Table header
  const cols = { date: m + 2, mins: m + 47, act: m + 70, score: m + 140, init: m + 167 };
  const rh = 10;
  doc.setFillColor(123, 47, 190);
  doc.rect(m, y, pw - 2 * m, rh, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text('Date', cols.date, y + 6.5);
  doc.text('Minutes', cols.mins, y + 6.5);
  doc.text('Activity', cols.act, y + 6.5);
  doc.text('Correct / Total', cols.score, y + 6.5);
  doc.text('Parent Initial', cols.init, y + 6.5);
  y += rh;

  let totalMins = 0;
  const tableTop = y;
  days.forEach((day, i) => {
    const ds = new Date(day); ds.setHours(0, 0, 0, 0);
    const de = new Date(day); de.setHours(23, 59, 59, 999);
    const dl = (tracker.log || []).filter(e => e.t >= ds.getTime() && e.t <= de.getTime());
    doc.setFillColor(i % 2 === 0 ? 252 : 246, i % 2 === 0 ? 252 : 248, i % 2 === 0 ? 255 : 254);
    doc.rect(m, y, pw - 2 * m, rh, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const dayStr = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    doc.setTextColor(30, 30, 30);
    doc.text(dayStr, cols.date, y + 6.5);
    if (dl.length > 0) {
      const ts = dl.map(e => e.t).sort((a, b) => a - b);
      const span = (ts[ts.length - 1] - ts[0]) / 60000;
      // Cap at 3 min/item to prevent multi-session days inflating the total
      const est = Math.max(1, Math.min(Math.round(span + dl.length * 0.25), dl.length * 3));
      totalMins += est;
      doc.text(String(est), cols.mins + 8, y + 6.5, { align: 'center' });
      const modes = [...new Set(dl.map(e => e.m))];
      doc.text(modes.map(md => modeLabels[md] || md).join(', '), cols.act, y + 6.5);
      const corr = dl.filter(e => e.c).length;
      doc.text(corr + ' / ' + dl.length, cols.score + 10, y + 6.5, { align: 'center' });
    } else {
      doc.setTextColor(180, 180, 195);
      doc.text('-', cols.mins + 8, y + 6.5, { align: 'center' });
      doc.text('No practice recorded', cols.act, y + 6.5);
      doc.text('-', cols.score + 10, y + 6.5, { align: 'center' });
      doc.setTextColor(30, 30, 30);
    }
    doc.setDrawColor(180, 180, 200);
    doc.setFillColor(255, 255, 255);
    doc.rect(cols.init, y + 1.5, 22, 7, 'FD');
    y += rh;
  });
  doc.setDrawColor(200, 200, 215);
  doc.rect(m, tableTop, pw - 2 * m, 7 * rh, 'S');
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.text('Total minutes practiced this week: ' + totalMins, m, y);
  y += 12;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Parent/Guardian Signature: _________________________________', m, y);
  doc.text('Date: _______________', pw - m - 52, y);
  y += 16;

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 120);
  doc.text(levelName + '  |  ' + lv.grade + '  |  Lexile: ' + lv.lexile + '  |  F&P: ' + lv.fp, m, y);

  doc.setDrawColor(220, 220, 230);
  doc.line(m, 265, pw - m, 265);
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 170);
  doc.text('Generated by Finley (finleyreads.com)  |  Built on the Science of Reading', pw / 2, 270, { align: 'center' });

  doc.save('finley-reading-log-' + days[0].toISOString().slice(0, 10) + '.pdf');
}

function generateReportCardPDF(name, age, tracker, childId) {
  
  
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pw = 215.9, m = 20;
  const lv = LEVELS[tracker.currentLevel] || LEVELS[3];
  const levelName = getLevelName(tracker.currentLevel);
  const bkt = tracker.bkt || {};
  const wm = tracker.wordMastery || {};
  const now = new Date();
  const thirtyAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const monthLog = (tracker.log || []).filter(e => e.t >= thirtyAgo.getTime());
  const sessionDays = new Set(monthLog.map(e => new Date(e.t).toDateString())).size;
  const estMins = Math.max(sessionDays, Math.round(monthLog.length * 0.3));
  const masteredWords = Object.entries(wm).filter(([, v]) => isWordMastered(v)).map(([k]) => k);
  const inProgWords = Object.entries(wm).filter(([, v]) => v && !isWordMastered(v) && v.exposures > 0).map(([k]) => k);
  const streak = computeStreak(tracker.log);
  const pace = getPace(tracker.currentLevel, age);
  const curSkills = SKILL_MAP[tracker.currentLevel] || [];
  const skName = (sk) => sk.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/ Mastery$/, ' (Advanced)').replace(/ Applied$/, ' (Applied)');
  const bktLabel = (p) => p >= 0.95 ? 'Mastered' : p >= 0.80 ? 'Almost There' : p >= 0.50 ? 'Building' : 'Starting';
  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const phaseDescs = {
    'Pre-alphabetic': 'Learning that letters represent sounds — the foundation of all reading.',
    'Partial alphabetic': 'Beginning to connect letters to sounds, especially at word beginnings.',
    'Full alphabetic': 'Can sound out most words letter by letter — building speed and fluency.',
    'Consolidated alphabetic': 'Recognizes letter patterns and chunks. Reads fluently with focus on meaning.'
  };

  const drawHdr = (pg, sub) => {
    doc.setFillColor(123, 47, 190);
    doc.rect(m, 14, pw - 2 * m, 20, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('FINLEY PROGRESS REPORT', pw / 2, 23, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text('finleyreads.com  |  Page ' + pg + ' of 4', pw / 2, 30, { align: 'center' });
    if (sub) {
      doc.setTextColor(30, 30, 30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(sub, m, 44);
      doc.setDrawColor(255, 107, 53);
      doc.setLineWidth(0.5);
      doc.line(m, 46, pw - m, 46);
      doc.setLineWidth(0.2);
    }
  };

  const drawFtr = (pg) => {
    doc.setDrawColor(220, 220, 230);
    doc.line(m, 264, pw - m, 264);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 170);
    doc.text('Built on the Science of Reading  |  Bayesian Knowledge Tracing  |  Ehri\'s Phases of Word Reading', pw / 2, 269, { align: 'center' });
    doc.text('Generated ' + fmtDate(now) + ' by Finley (finleyreads.com)  |  Page ' + pg + ' of 4', pw / 2, 273, { align: 'center' });
  };

  // ── PAGE 1: OVERVIEW ──────────────────────────────────
  drawHdr(1, null);
  let y = 38;

  doc.setFillColor(250, 250, 255);
  doc.rect(m, y, pw - 2 * m, 26, 'F');
  doc.setDrawColor(200, 200, 220);
  doc.rect(m, y, pw - 2 * m, 26, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 50);
  doc.text(name + "'s Reading Report", pw / 2, y + 9, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Age: ' + age + '  |  Period: ' + fmtDate(thirtyAgo) + ' - ' + fmtDate(now), pw / 2, y + 17, { align: 'center' });
  doc.text('Generated: ' + fmtDate(now), pw / 2, y + 23, { align: 'center' });
  y += 32;

  doc.setFillColor(255, 240, 235);
  doc.rect(m, y, pw - 2 * m, 28, 'F');
  doc.setDrawColor(255, 107, 53);
  doc.rect(m, y, pw - 2 * m, 28, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(255, 107, 53);
  doc.text(levelName, pw / 2, y + 11, { align: 'center' });
  doc.setFontSize(11);
  doc.setTextColor(80, 80, 100);
  doc.text(lv.grade + ' equivalent  |  ' + pace.label + ' for age ' + age, pw / 2, y + 19, { align: 'center' });
  doc.setFontSize(9);
  doc.text('Lexile: ' + lv.lexile + '  |  F&P: ' + lv.fp + '  |  Phase: ' + lv.ehri, pw / 2, y + 25, { align: 'center' });
  y += 34;

  const stats = [
    { l: 'Practice Days', v: String(sessionDays) },
    { l: 'Est. Minutes', v: estMins + ' min' },
    { l: 'Words Mastered', v: String(masteredWords.length) },
    { l: 'Day Streak', v: String(streak) }
  ];
  const boxW = (pw - 2 * m - 9) / 4;
  stats.forEach((s, i) => {
    const bx = m + i * (boxW + 3);
    doc.setFillColor(245, 245, 255);
    doc.rect(bx, y, boxW, 22, 'F');
    doc.setDrawColor(210, 210, 230);
    doc.rect(bx, y, boxW, 22, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(123, 47, 190);
    doc.text(s.v, bx + boxW / 2, y + 13, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 120);
    doc.text(s.l, bx + boxW / 2, y + 19.5, { align: 'center' });
  });
  y += 28;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 50);
  doc.text('What ' + name + ' is Working On', m, y);
  doc.setDrawColor(220, 220, 230);
  doc.line(m, y + 2, pw - m, y + 2);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 80);
  doc.text('Current skill focus: ' + lv.skill, m, y);
  y += 6;
  doc.text('Reading phase: ' + lv.ehri + ' - ' + (phaseDescs[lv.ehri] || lv.ehri), m, y, { maxWidth: pw - 2 * m });
  y += 12;

  if (tracker.aiProfile) {
    doc.setFillColor(243, 229, 245);
    doc.rect(m, y, pw - 2 * m, 32, 'F');
    doc.setDrawColor(123, 47, 190);
    doc.rect(m, y, pw - 2 * m, 32, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(80, 20, 140);
    doc.text('AI Learning Profile (Claude AI)', m + 4, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(50, 30, 80);
    const aiLines = tracker.aiProfile.parentNote
      ? doc.splitTextToSize(tracker.aiProfile.parentNote, pw - 2 * m - 8).slice(0, 2)
      : [];
    if (aiLines.length) doc.text(aiLines, m + 4, y + 14);
    if (tracker.aiProfile.strengths && tracker.aiProfile.strengths.length)
      doc.text('Strengths: ' + tracker.aiProfile.strengths.slice(0, 2).join(', '), m + 4, y + 25);
    if (tracker.aiProfile.focus)
      doc.text('Focus: ' + tracker.aiProfile.focus, m + 4, y + 30);
    y += 36;
  }

  drawFtr(1);

  // ── PAGE 2: SKILL MASTERY ─────────────────────────────
  doc.addPage();
  drawHdr(2, 'Skill Mastery - ' + levelName);
  y = 52;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 100);
  doc.text('Skills tracked via Bayesian Knowledge Tracing (BKT). Mastery threshold: 95%. Dotted line marks mastery target.', m, y);
  y += 12;

  curSkills.forEach((sk, i) => {
    const pL = bkt[sk] || 0;
    const pct = Math.min(100, Math.round(pL * 100));
    const barW = pw - 2 * m - 50;
    const sc = pL >= 0.95 ? [74, 174, 80] : pL >= 0.80 ? [245, 158, 11] : pL >= 0.50 ? [255, 107, 53] : [156, 163, 175];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 50);
    doc.text(skName(sk), m, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(sc[0], sc[1], sc[2]);
    doc.text(bktLabel(pL) + ' (' + pct + '%)', pw - m, y, { align: 'right' });
    y += 5;
    doc.setFillColor(225, 225, 238);
    doc.rect(m, y, barW, 5, 'F');
    if (pct > 0) { doc.setFillColor(sc[0], sc[1], sc[2]); doc.rect(m, y, barW * pct / 100, 5, 'F'); }
    doc.setDrawColor(100, 100, 140);
    doc.line(m + barW * 0.95, y - 1, m + barW * 0.95, y + 6);
    y += 11;
    if (y > 240 && i < curSkills.length - 1) { doc.addPage(); drawHdr(2, 'Skill Mastery (continued)'); y = 52; }
  });

  if (y < 195) {
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 50);
    doc.text('All-Level Progress Summary', m, y);
    doc.setDrawColor(220, 220, 230);
    doc.line(m, y + 2, pw - m, y + 2);
    y += 8;
    const colCnt = 5;
    const cellW = (pw - 2 * m - (colCnt - 1) * 3) / colCnt;
    for (let lNum = 1; lNum <= 15; lNum++) {
      const lSkills = SKILL_MAP[lNum] || [];
      const lProg = lSkills.length > 0 ? lSkills.reduce((s, sk) => s + (bkt[sk] || 0), 0) / lSkills.length : 0;
      const lMast = lSkills.length > 0 && lSkills.every(sk => (bkt[sk] || 0) >= 0.95);
      const col = (lNum - 1) % colCnt;
      const row = Math.floor((lNum - 1) / colCnt);
      const cx = m + col * (cellW + 3);
      const cy = y + row * 16;
      const r = Math.round(lProg * (lMast ? 74 : 120) + (1 - lProg) * 225);
      const g = Math.round(lProg * (lMast ? 174 : 170) + (1 - lProg) * 225);
      const b = Math.round(lProg * (lMast ? 80 : 240) + (1 - lProg) * 238);
      doc.setFillColor(r, g, b);
      doc.rect(cx, cy, cellW, 13, 'F');
      doc.setDrawColor(200, 200, 220);
      doc.rect(cx, cy, cellW, 13, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(40, 40, 70);
      doc.text('L' + lNum, cx + cellW / 2, cy + 6, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text(Math.round(lProg * 100) + '%', cx + cellW / 2, cy + 11, { align: 'center' });
    }
  }

  drawFtr(2);

  // ── PAGE 3: VOCABULARY ────────────────────────────────
  doc.addPage();
  drawHdr(3, 'Vocabulary Progress');
  y = 52;
  const wColCnt = 4;
  const wColW = (pw - 2 * m - (wColCnt - 1) * 4) / wColCnt;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(74, 174, 80);
  doc.text('Words Mastered (' + masteredWords.length + ')', m, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(80, 80, 80);
  doc.text('Box 4-5  |  8+ exposures  |  90%+ accuracy', m, y + 5);
  y += 10;
  if (masteredWords.length > 0) {
    masteredWords.slice(0, 60).forEach((word, i) => {
      const col = i % wColCnt, row = Math.floor(i / wColCnt);
      const wx = m + col * (wColW + 4), wy = y + row * 7;
      doc.setFillColor(240, 255, 244);
      doc.rect(wx, wy - 4, wColW, 6, 'F');
      doc.setTextColor(30, 100, 50);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(word, wx + 2, wy);
    });
    y += Math.ceil(Math.min(masteredWords.length, 60) / wColCnt) * 7 + 6;
  } else {
    doc.setTextColor(150, 150, 160);
    doc.setFontSize(9);
    doc.text('No words fully mastered yet - keep practicing!', m, y);
    y += 10;
  }

  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255, 107, 53);
  doc.text('Words in Progress (' + inProgWords.length + ')', m, y);
  y += 6;
  if (inProgWords.length > 0) {
    inProgWords.slice(0, 40).forEach((word, i) => {
      const col = i % wColCnt, row = Math.floor(i / wColCnt);
      const wx = m + col * (wColW + 4), wy = y + row * 7;
      doc.setFillColor(255, 240, 235);
      doc.rect(wx, wy - 4, wColW, 6, 'F');
      doc.setTextColor(150, 50, 20);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(word, wx + 2, wy);
    });
    y += Math.ceil(Math.min(inProgWords.length, 40) / wColCnt) * 7 + 6;
  } else {
    doc.setTextColor(150, 150, 160);
    doc.setFontSize(9);
    doc.text('No words in progress yet.', m, y);
    y += 10;
  }

  const sp = getSpellingMastery(childId, tracker.wordMastery);
  if (sp.total > 0 && y < 230) {
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 50);
    doc.text("This Week's Spelling Words (" + sp.mastered + '/' + sp.total + ' mastered)', m, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    sp.words.forEach((w, i) => {
      const col = i % 3, row = Math.floor(i / 3);
      const wx = m + col * ((pw - 2 * m) / 3), wy = y + row * 7;
      doc.setTextColor(w.masteredInApp ? 30 : 100, w.masteredInApp ? 100 : 100, w.masteredInApp ? 50 : 120);
      doc.text((w.masteredInApp ? '[*] ' : '[ ] ') + w.word, wx, wy);
    });
    y += Math.ceil(sp.words.length / 3) * 7 + 4;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 140);
    doc.text('[*] = mastered in Finley (3+ correct answers)', m, y);
  }

  drawFtr(3);

  // ── PAGE 4: RECOMMENDATIONS ───────────────────────────
  doc.addPage();
  drawHdr(4, 'Recommendations & Next Steps');
  y = 52;

  if (tracker.aiProfile) {
    doc.setFillColor(243, 229, 245);
    doc.rect(m, y, pw - 2 * m, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(80, 20, 140);
    doc.text('AI Session Analysis (Powered by Claude AI)', m + 3, y + 4.5);
    y += 9;
    if (tracker.aiProfile.strengths && tracker.aiProfile.strengths.length) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(74, 174, 80);
      doc.text('Strengths:', m, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(30, 80, 40);
      tracker.aiProfile.strengths.forEach(s => { doc.text('- ' + s, m + 4, y); y += 6; });
      y += 2;
    }
    if (tracker.aiProfile.weaknesses && tracker.aiProfile.weaknesses.length) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 107, 53);
      doc.text('Areas to Develop:', m, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(100, 50, 20);
      tracker.aiProfile.weaknesses.forEach(w => { doc.text('- ' + w, m + 4, y); y += 6; });
      y += 2;
    }
    if (tracker.aiProfile.focus) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 50);
      doc.text('Focus for Next Session:', m, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(50, 50, 80);
      doc.text(tracker.aiProfile.focus, m + 4, y);
      y += 8;
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 50);
  doc.text('What to Practice at Home', m, y);
  doc.setDrawColor(220, 220, 230);
  doc.line(m, y + 2, pw - m, y + 2);
  y += 8;
  const homeActs = [
    'Read aloud together 15-20 minutes daily - let ' + name + ' read to you',
    'Point out spelling words in everyday text (signs, menus, cereal boxes)',
    'Ask "what do you think happens next?" while reading stories together',
    'Celebrate effort, not just correct answers - growth mindset matters',
    (tracker.weakWords && tracker.weakWords.length > 0)
      ? 'Practice these challenge words: ' + tracker.weakWords.slice(0, 5).join(', ')
      : 'Review the Words in Progress list on Page 3 using flashcards',
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(50, 50, 70);
  homeActs.forEach(a => {
    const lines = doc.splitTextToSize('- ' + a, pw - 2 * m - 4);
    doc.text(lines, m + 2, y);
    y += lines.length * 6 + 1;
  });

  y += 6;
  const nextLv = Math.min(tracker.currentLevel + 1, 15);
  const nlv = LEVELS[nextLv];
  doc.setFillColor(240, 249, 255);
  doc.rect(m, y, pw - 2 * m, 24, 'F');
  doc.setDrawColor(0, 180, 216);
  doc.rect(m, y, pw - 2 * m, 24, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 100, 150);
  doc.text('Working Toward ' + getLevelName(nextLv) + (nlv ? ' - ' + nlv.name : ''), m + 4, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 60, 100);
  if (nlv) {
    doc.text('Next skills: ' + nlv.skill + '  |  ' + nlv.grade + '  |  Phase: ' + nlv.ehri, m + 4, y + 15);
    const gaps = (SKILL_MAP[nextLv] || []).map(sk => bkt[sk] || 0);
    const avgGap = gaps.length > 0 ? 1 - gaps.reduce((s, v) => s + v, 0) / gaps.length : 0;
    doc.text('Skill gap to next level: ' + Math.round(avgGap * 100) + '% development remaining', m + 4, y + 21);
  }
  y += 30;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(120, 120, 140);
  const note = 'This report was generated by Finley, an AI-powered adaptive reading app. Progress data is based on in-app practice using Bayesian Knowledge Tracing and Leitner spaced repetition. For comprehensive assessment, supplement with your child\'s school reading evaluations.';
  doc.text(doc.splitTextToSize(note, pw - 2 * m), m, y);

  drawFtr(4);
  doc.save('finley-progress-report-' + name.toLowerCase().replace(/\s+/g, '-') + '-' + now.toISOString().slice(0, 10) + '.pdf');
}

export function Dash({tracker,name,age,onClose,minutesToday,childId}){
  const[reportWeek,setReportWeek]=useState(0);
  const[emailTarget,setEmailTarget]=useState(null);const[emailSending,setEmailSending]=useState(false);const[emailSent,setEmailSent]=useState(null);
  const[emailAddr,setEmailAddr]=useState(()=>getParentEmail());
  const lv=LEVELS[tracker.currentLevel]||LEVELS[3];const ms=tracker.ms||{phonics:{c:0,t:0},sight:{c:0,t:0},stories:{c:0,t:0},bubbles:{c:0,t:0}};const total=Object.values(ms).reduce((s,m)=>s+m.t,0);const correct=Object.values(ms).reduce((s,m)=>s+m.c,0);const pace=getPace(tracker.currentLevel,age);
  const levelName = getLevelName(tracker.currentLevel);
  const tier=getAppTier();const canRC=tier==='plus'||tier==='family';
  const wkOpts=[0,-1,-2,-3].map(o=>{const d=getWeekDates(o);const wStr=d[0].toLocaleDateString('en-US',{month:'short',day:'numeric'})+' - '+d[6].toLocaleDateString('en-US',{month:'short',day:'numeric'});return{v:o,l:o===0?'This Week ('+wStr+')':o===-1?'Last Week ('+wStr+')':o===-2?'2 Weeks Ago ('+wStr+')':'3 Weeks Ago ('+wStr+')'};});
  const bkt=tracker.bkt||{};const wm=tracker.wordMastery||{};
  const curSkills=SKILL_MAP[tracker.currentLevel]||[];
  const masteredWords=Object.values(wm).filter(e=>isWordMastered(e)).length;
  const inProgressWords=Object.values(wm).filter(e=>e&&!isWordMastered(e)&&e.exposures>0).length;
  const bktLabel=(p)=>p>=0.95?'Mastered':p>=0.80?'Almost There':p>=0.50?'Building':'Starting';
  const bktColor=(p)=>p>=0.95?C.success:p>=0.80?'#F59E0B':p>=0.50?C.primary:C.textLight;
  const skillName=(sk)=>sk.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()).replace(/ Mastery$/,' (Advanced)').replace(/ Applied$/,' (Applied)');
  const skillDesc={
    "Letter sounds":"Recognizing individual letter sounds — the foundation of all reading.",
    "CVC words":"Sounding out simple 3-letter words like cat, dog, run.",
    "CVC & sight words":"Blending simple words and memorizing common words by sight.",
    "Word families":"Recognizing patterns like -at, -an, -ig to read new words faster.",
    "Blends & digraphs":"Reading sounds like bl, cr, sh, th — two letters making one sound.",
    "Sentences & fluency":"Reading full sentences smoothly without stopping at each word.",
    "Paragraphs":"Reading and understanding short paragraphs with multiple sentences.",
    "Full stories":"Following a complete short story from beginning to end.",
    "Vocabulary & inference":"Using clues in the text to figure out what words mean and what happens next.",
    "Advanced comprehension":"Understanding characters, plot, and deeper meaning in longer stories.",
    "Context clues & roots":"Using Greek and Latin word roots (like tele = far) to decode unfamiliar words.",
    "Theme & text evidence":"Identifying the main idea and pointing to specific sentences that prove it.",
    "Figurative language":"Understanding metaphors, similes, and phrases that don't mean what they literally say.",
    "Inference & argument":"Reading between the lines and evaluating whether an author's argument makes sense.",
    "Critical analysis":"Comparing texts, identifying bias, and forming their own supported opinions."
  };
  const phaseDesc={
    "Pre-alphabetic":"Just starting — learning that letters represent sounds.",
    "Partial alphabetic":"Beginning to connect some letters to sounds, especially at the start of words.",
    "Full alphabetic":"Can sound out most words letter by letter. Building speed.",
    "Consolidated alphabetic":"Recognizes common letter patterns and chunks. Reads fluently with focus on meaning."
  };
  return <motion.div {...fadeIn} className="px-6 py-5">
    <div className="mb-4 flex items-center justify-between"><h2 className="m-0 font-finley-display text-3xl text-finley-primary">📊 {name}'s Progress</h2><motion.button {...tapScale} onClick={onClose} className="min-h-[48px] rounded-finley-full bg-finley-bg-card px-4 py-2 text-sm text-finley-text-light shadow-finley-sm">✕</motion.button></div>

    {/* Finley Reading Level */}
    <div className="mb-4 rounded-finley-lg border-2 p-4 text-center shadow-finley-md" style={{background:pace.bg,borderColor:`${pace.color}33`}}><div className="text-4xl">{pace.emoji}</div><div className="mb-1 font-finley-display text-3xl" style={{color:lv.color}}>{levelName}</div><div className="font-finley-display text-lg" style={{color:pace.color}}>{pace.label}</div><div className="text-sm text-finley-text-light">Age {age} • Reading at {lv.grade} Level</div></div>

    {/* Today's Session */}
    {minutesToday>0&&(()=>{const todayStart=new Date();todayStart.setHours(0,0,0,0);const ts0=todayStart.getTime();const todayLog=(tracker.log||[]).filter(e=>e.t&&e.t>=ts0);const tC=todayLog.filter(r=>r.c).length;const tT=todayLog.length;return <div className="mb-4 rounded-finley-lg bg-finley-bg-card p-4 shadow-finley-sm"><div className="mb-1 font-finley-display text-lg text-finley-text">Today's Practice</div><div className="text-sm text-finley-text-light">Today: {minutesToday} min • {tT>0?tC+' of '+tT+' correct ('+Math.round((tC/tT)*100)+'%)':'No answers yet'}</div></div>;})()}

    {/* Skill Mastery Bars */}
    {curSkills.length>0&&<div style={{background:'#F8F9FA',borderRadius:16,padding:16,marginBottom:14,border:'2px solid #F0F0F5'}}>
      <div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:12}}>Skill Mastery — {levelName}</div>
      {curSkills.map(sk=>{const p=bkt[sk]||0;const pct=Math.round(p*100);return <div key={sk} style={{marginBottom:10}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}><span style={{fontFamily:N,fontSize:12,color:C.text}}>{skillName(sk)}</span><span style={{fontFamily:N,fontSize:11,color:bktColor(p),fontWeight:700}}>{bktLabel(p)}</span></div>
        <div style={{position:'relative',height:12,background:'#E8E8E8',borderRadius:8,overflow:'hidden'}}><div style={{height:'100%',width:pct+'%',borderRadius:8,background:pct>=95?C.success:pct>=50?`linear-gradient(90deg, ${C.primary}, #F59E0B)`:C.primary,transition:'width 0.5s'}}/><div style={{position:'absolute',left:'95%',top:0,bottom:0,width:1,borderLeft:'2px dotted rgba(0,0,0,0.2)'}}/></div>
      </div>;})}
    </div>}

    {/* Words Mastered */}
    <div style={{background:'#F8F9FA',borderRadius:16,padding:16,marginBottom:14,border:'2px solid #F0F0F5',display:'flex',alignItems:'center',gap:16}}>
      <div style={{textAlign:'center',flex:1}}><div style={{fontFamily:F,fontSize:28,color:C.success}}>{masteredWords}</div><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>Words Mastered</div></div>
      <div style={{width:1,height:36,background:'#E5E7EB'}}/>
      <div style={{textAlign:'center',flex:1}}><div style={{fontFamily:F,fontSize:28,color:C.primary}}>{inProgressWords}</div><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>In Progress</div></div>
    </div>

    {/* Spelling Progress */}
    {(()=>{const sp=getSpellingMastery(childId,tracker.wordMastery);if(!sp.total)return null;return <div style={{background:'#F8F9FA',borderRadius:16,padding:16,marginBottom:14,border:'2px solid #F0F0F5'}}>
      <div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:10}}>{"📝"} Spelling Progress — This Week</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>{sp.words.map(w=><div key={w.word} style={{display:'flex',alignItems:'center',gap:3,padding:'4px 10px',borderRadius:20,background:w.masteredInApp?C.successLight:C.primaryLight,border:`2px solid ${w.masteredInApp?C.success:C.primary}22`,fontFamily:F,fontSize:13,color:w.masteredInApp?C.success:C.primary}}>{w.word}{w.masteredInApp&&<span style={{fontSize:11,marginLeft:2}}>{"✓"}</span>}</div>)}</div>
      <div style={{fontFamily:N,fontSize:11,color:C.textLight}}>{sp.mastered}/{sp.total} mastered in app (3+ correct answers)</div>
    </div>;})()}

    {/* Reading Level — plain English */}
    <div style={{background:"#F8F9FA",borderRadius:16,padding:16,marginBottom:14,border:"2px solid #F0F0F5"}}>
      <div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:10}}>📖 Reading Level</div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
        <div style={{width:48,height:48,borderRadius:14,background:`${lv.color}18`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F,fontSize:20,color:lv.color,fontWeight:700,border:`2px solid ${lv.color}33`}}>📚</div>
        <div><div style={{fontFamily:F,fontSize:17,color:lv.color}}>{levelName}</div><div style={{fontFamily:N,fontSize:13,color:C.textMid}}>Typical for {lv.grade} students</div></div>
      </div>
      <div style={{fontFamily:N,fontSize:13,color:C.textMid,lineHeight:1.6,marginBottom:10}}>
        <strong style={{color:C.text}}>What {name} is working on:</strong> {skillDesc[lv.skill]||lv.skill}
      </div>
      <div style={{fontFamily:N,fontSize:13,color:C.textMid,lineHeight:1.6}}>
        <strong style={{color:C.text}}>Reading development phase:</strong> {phaseDesc[lv.ehri]||lv.ehri}
      </div>
    </div>

    {/* Benchmarks — with explanations */}
    <div style={{background:"#F8F9FA",borderRadius:16,padding:16,marginBottom:14,border:"2px solid #F0F0F5"}}>
      <div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:10}}>📏 How This Compares</div>
      <div style={{fontFamily:N,fontSize:13,color:C.textMid,lineHeight:1.8}}>
        <div style={{padding:"6px 0",borderBottom:"1px solid #F0F0F5"}}><strong style={{color:C.text}}>Grade:</strong> <span style={{color:"#1E88E5",fontWeight:700}}>{lv.grade}</span> — the school grade where this reading level is typically taught.</div>
        <div style={{padding:"6px 0",borderBottom:"1px solid #F0F0F5"}}><strong style={{color:C.text}}>F&P Level:</strong> <span style={{color:C.success,fontWeight:700}}>{lv.fp}</span> — Fountas & Pinnell, the A-Z scale teachers use to pick "just right" books. Ask your child's teacher what level they're reading at in school to compare.</div>
        <div style={{padding:"6px 0"}}><strong style={{color:C.text}}>Lexile:</strong> <span style={{color:C.accent,fontWeight:700}}>{lv.lexile}</span> — a number that matches readers to books. You'll see Lexile scores on the back of many children's books and on sites like Amazon.</div>
      </div>
    </div>

    {/* Stats */}
    <div className="mb-4 rounded-finley-lg bg-finley-bg-card p-4 shadow-finley-md"><div className="mb-3 font-finley-display text-lg text-finley-text">📈 Stats</div><div className="grid grid-cols-3 gap-3">{[{l:"Answers",v:total,c:"var(--color-finley-accent)"},{l:"Accuracy",v:total>0?`${Math.round((correct/total)*100)}%`:"-",c:"var(--color-finley-success)"},{l:"Today",v:(minutesToday||0)+" min",c:"var(--color-finley-primary)"}].map(s=><div key={s.l} className="text-center"><div className="font-finley-display text-3xl" style={{color:s.c}}>{s.v}</div><div className="text-xs text-finley-text-light">{s.l}</div></div>)}</div></div>

    {/* Weak words */}
    {tracker.weakWords?.length>0&&<div style={{background:"#F8F9FA",borderRadius:16,padding:16,marginBottom:14,border:"2px solid #F0F0F5"}}><div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:4}}>🎯 Needs Practice</div><div style={{fontFamily:N,fontSize:12,color:C.textMid,marginBottom:10}}>Words {name} got wrong recently. These show up in Bubble Pop for extra practice.</div><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{tracker.weakWords.slice(0,12).map(w=><span key={w} style={{padding:"4px 12px",borderRadius:8,fontSize:14,fontFamily:F,background:C.dangerLight,color:C.danger,border:`1px solid ${C.danger}33`}}>{w}</span>)}</div></div>}

    {/* Verify */}
    <div style={{background:"#F8F9FA",borderRadius:16,padding:16,marginBottom:14,border:`2px solid ${C.primary}33`}}><div style={{fontFamily:F,fontSize:15,color:C.primary,marginBottom:4}}>✅ Verify Progress</div><p style={{fontFamily:N,fontSize:12,color:C.textMid,margin:"0 0 10px"}}>Want a second opinion? These free tools independently assess your child's reading level:</p>
      <a href="https://readtheory.org" target="_blank" rel="noopener" style={{display:"block",padding:"8px 12px",background:C.bg,borderRadius:10,border:"1px solid #E5E7EB",textDecoration:"none",marginBottom:6}}><div style={{fontFamily:F,fontSize:13,color:C.text}}>📊 ReadTheory</div><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>Free reading assessment — gives a Lexile score you can compare</div></a>
      <a href="https://roar.stanford.edu" target="_blank" rel="noopener" style={{display:"block",padding:"8px 12px",background:C.bg,borderRadius:10,border:"1px solid #E5E7EB",textDecoration:"none"}}><div style={{fontFamily:F,fontSize:13,color:C.text}}>🎓 Stanford ROAR</div><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>Research-backed assessment from Stanford University</div></a>
    </div>

    {/* AI Learning Profile */}
    {tracker.aiProfile&&<div style={{background:"#F8F9FA",borderRadius:16,padding:16,marginBottom:14,border:`2px solid ${C.secondary}33`}}>
      <div style={{fontFamily:F,fontSize:15,color:C.secondary,marginBottom:10}}>🧠 AI Learning Profile</div>
      {tracker.aiProfile.strengths&&tracker.aiProfile.strengths.length>0&&<div style={{marginBottom:8}}><div style={{fontFamily:N,fontSize:12,color:C.success,fontWeight:700,marginBottom:4}}>💪 Strengths</div><div style={{fontFamily:N,fontSize:13,color:C.textMid}}>{tracker.aiProfile.strengths.join(", ")}</div></div>}
      {tracker.aiProfile.weaknesses&&tracker.aiProfile.weaknesses.length>0&&<div style={{marginBottom:8}}><div style={{fontFamily:N,fontSize:12,color:C.primary,fontWeight:700,marginBottom:4}}>🎯 Working On</div><div style={{fontFamily:N,fontSize:13,color:C.textMid}}>{tracker.aiProfile.weaknesses.join(", ")}</div></div>}
      {tracker.aiProfile.focus&&<div style={{marginBottom:8}}><div style={{fontFamily:N,fontSize:12,color:C.accent,fontWeight:700,marginBottom:4}}>📌 Next Session Focus</div><div style={{fontFamily:N,fontSize:13,color:C.textMid}}>{tracker.aiProfile.focus}</div></div>}
      {tracker.aiProfile.pacing&&<div style={{marginBottom:8}}><div style={{fontFamily:N,fontSize:12,color:C.textLight,fontWeight:700,marginBottom:4}}>⏱️ Pacing</div><div style={{fontFamily:N,fontSize:13,color:C.textMid}}>{tracker.aiProfile.pacing==="slow"?"Gentle — building confidence before advancing":tracker.aiProfile.pacing==="fast"?"Accelerated — ready for challenges":"Standard — steady progression"}</div></div>}
      {tracker.aiProfile.parentNote&&<div style={{background:C.secondaryLight,borderRadius:10,padding:"10px 14px",border:`1px solid ${C.secondary}22`}}><div style={{fontFamily:N,fontSize:13,color:C.text,lineHeight:1.5}}>{tracker.aiProfile.parentNote}</div></div>}
    </div>}

    {/* Reports */}
    <div style={{background:'#F8F9FA',borderRadius:16,padding:16,marginBottom:14,border:`2px solid ${C.accent}44`}}>
      <div style={{fontFamily:F,fontSize:15,color:C.text,marginBottom:12}}>📄 School Reports</div>
      {/* Reading Log */}
      <div style={{marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
          <span style={{fontFamily:F,fontSize:13,color:C.textMid,flex:1}}>📋 Weekly Reading Log</span>
          <select value={reportWeek} onChange={e=>setReportWeek(Number(e.target.value))} style={{fontFamily:N,fontSize:11,padding:'3px 8px',borderRadius:8,border:'1px solid #E5E7EB',background:'#fff',color:C.text}}>
            {wkOpts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div style={{display:'flex',gap:8,marginBottom:6}}>
          <button onClick={()=>generateReadingLogPDF(name,age,tracker,childId,reportWeek)} style={{flex:1,padding:'8px 0',fontFamily:F,fontSize:13,background:C.primary,color:'#fff',border:'none',borderRadius:10,cursor:'pointer',fontWeight:600}}>⬇ Download PDF</button>
          <button onClick={()=>setEmailTarget(emailTarget==='log'?null:'log')} style={{flex:1,padding:'8px 0',fontFamily:F,fontSize:13,background:emailTarget==='log'?C.secondary:'#F0F0F5',color:emailTarget==='log'?'#fff':C.textMid,border:'none',borderRadius:10,cursor:'pointer',fontWeight:600}}>✉ Email</button>
        </div>
        {emailTarget==='log'&&<div style={{background:'#F0F9FF',borderRadius:10,padding:'10px 12px',border:'1px solid #BAD6F2'}}>
          <div style={{fontFamily:N,fontSize:12,color:C.textMid,marginBottom:6}}>Email the weekly reading log as an HTML report.</div>
          <input type="email" value={emailAddr} onChange={e=>setEmailAddr(e.target.value)} placeholder="your@email.com" style={{fontFamily:N,fontSize:12,padding:'6px 10px',borderRadius:8,border:'1px solid #E5E7EB',width:'100%',boxSizing:'border-box',marginBottom:6}}/>
          <button onClick={async()=>{if(!emailAddr||!emailAddr.includes('@'))return;setEmailSending(true);try{const logHtml=buildReadingLogHTML(name,age,tracker,childId,reportWeek);const result=await sendEmail(emailAddr,{subject:name+"'s Weekly Reading Log",html:logHtml});saveParentEmail(emailAddr);if(result.error){setEmailSent('error');}else{setEmailSent('log');setEmailTarget(null);}}catch{setEmailSent('error');}setEmailSending(false);setTimeout(()=>setEmailSent(null),3000);}} disabled={emailSending} style={{fontFamily:F,fontSize:12,padding:'6px 14px',background:emailSending?'#E5E7EB':C.primary,color:emailSending?C.textLight:'#fff',border:'none',borderRadius:8,cursor:emailSending?'default':'pointer',width:'100%'}}>{emailSending?'Sending...':'Send Reading Log'}</button>
        </div>}
      </div>
      {/* Report Card */}
      <div style={{borderTop:'1px solid #F0F0F5',paddingTop:12}}>
        <div style={{fontFamily:F,fontSize:13,color:C.textMid,marginBottom:8}}>📊 Monthly Progress Report</div>
        {canRC?<div>
          <div style={{display:'flex',gap:8,marginBottom:6}}>
            <button onClick={()=>generateReportCardPDF(name,age,tracker,childId)} style={{flex:1,padding:'8px 0',fontFamily:F,fontSize:13,background:C.secondary,color:'#fff',border:'none',borderRadius:10,cursor:'pointer',fontWeight:600}}>⬇ Download PDF</button>
            <button onClick={()=>setEmailTarget(emailTarget==='report'?null:'report')} style={{flex:1,padding:'8px 0',fontFamily:F,fontSize:13,background:emailTarget==='report'?'#1E88E5':'#F0F0F5',color:emailTarget==='report'?'#fff':C.textMid,border:'none',borderRadius:10,cursor:'pointer',fontWeight:600}}>✉ Email Teacher</button>
          </div>
          {emailTarget==='report'&&<div style={{background:'#F0F9FF',borderRadius:10,padding:'10px 12px',border:'1px solid #BAD6F2'}}>
            <div style={{fontFamily:N,fontSize:12,color:C.textMid,marginBottom:6}}>Email the progress report to a teacher or yourself.</div>
            <input type="email" value={emailAddr} onChange={e=>setEmailAddr(e.target.value)} placeholder="teacher@school.edu" style={{fontFamily:N,fontSize:12,padding:'6px 10px',borderRadius:8,border:'1px solid #E5E7EB',width:'100%',boxSizing:'border-box',marginBottom:6}}/>
            <button onClick={async()=>{if(!emailAddr||!emailAddr.includes('@'))return;setEmailSending(true);try{const reportHtml=buildReportCardHTML(name,age,tracker,childId);const result=await sendEmail(emailAddr,{subject:name+"'s Progress Report — Finley",html:reportHtml});if(result.error){setEmailSent('error');}else{setEmailSent('report');setEmailTarget(null);}}catch{setEmailSent('error');}setEmailSending(false);setTimeout(()=>setEmailSent(null),3000);}} disabled={emailSending} style={{fontFamily:F,fontSize:12,padding:'6px 14px',background:emailSending?'#E5E7EB':C.secondary,color:emailSending?C.textLight:'#fff',border:'none',borderRadius:8,cursor:emailSending?'default':'pointer',width:'100%'}}>{emailSending?'Sending...':'Send to Teacher'}</button>
          </div>}
        </div>:<div style={{fontFamily:N,fontSize:12,color:C.textLight,padding:'4px 0'}}>Upgrade to Plus or Family to generate progress reports.</div>}
      </div>
    </div>

    {emailSent==='error'&&<div style={{background:C.dangerLight,borderRadius:10,padding:'10px 14px',marginBottom:14,border:`2px solid ${C.danger}33`}}><span style={{fontFamily:N,fontSize:13,color:C.danger,fontWeight:600}}>Could not send email. Check your connection and try again.</span></div>}
    {emailSent&&emailSent!=='error'&&<div style={{background:C.successLight,borderRadius:10,padding:'10px 14px',marginBottom:14,border:`2px solid ${C.success}33`}}><span style={{fontFamily:N,fontSize:13,color:C.success,fontWeight:600}}>✅ Email sent!</span></div>}

    {/* Privacy */}
    <div className="rounded-finley-lg border-2 border-finley-secondary/20 bg-finley-secondary/10 p-4 shadow-finley-sm"><div className="mb-1 font-finley-display text-lg text-finley-secondary">🔒 Privacy</div><div className="text-sm leading-6 text-finley-text-light">No real names required. No child accounts. Data stored locally and securely synced to our servers. We never sell or share your child's data. COPPA-friendly.</div></div>
  </motion.div>;
}
